import { useLocation, useNavigate } from 'react-router-dom'
import React, { createContext, useEffect, useState, useContext } from "react";
import { Box, Container, Button } from "@mui/material";
import QMentisBreadCrumb from '../../custom-components/QMentisBreadCrumb';
import { Typography } from '@mui/material'
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import TestCaseUI from '../../output-components/test-automation/TestCaseImported';
import Feature from '../../output-components/test-automation/FeatureFilesCreated';
import InputForm from '../../output-components/test-automation/PageSource';
import PageClass from '../../output-components/test-automation/PageClass';
import Locators from '../../output-components/test-automation/Locators';
import StepDefinition from '../../output-components/test-automation/StepDefinition';
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { cypressTestFilesWithToolCalling, cypressPageClassAPIWithToolCalling, cypressProjectAPIWithToolCalling } from '../../../services/webServices';
import { LoadingModalContext } from "../../main-pages/ServicePage";
import UploadBox from '../input-pages/UploadBox';
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { downloadCypressProject, testDownloadCypressProject } from "../../download/cypressProjectDownload";
import TestEnvironmentForm from '../../output-components/test-automation/TestEnvironment';
import MessagePopup from '../../popup-components/MessagePopup';
import SuccessPopup from '../../output-components/test-automation/SuccessPopup';

export const TestCaseFlowDataContext = createContext(null);

const CypressJavaScriptNewProjectOutputPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [testEnvironment, setTestEnviorment] = useState({});
  const [testCase, setTestCase] = useState({});
  const [testFiles, setTestFiles] = useState('');
  const [pageSource, setPageSource] = useState('');
  const [locator, setLocator] = useState('');
  const [pageClass, setPageClass] = useState('');
  const [project, setProject] = useState('');
  const {setLoadingState} = useContext(LoadingModalContext);

  const [confirmed, setConfirmed] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [projectType, setProjectType] = useState('');
  const [zipBlob, setZipBlob] = useState(null); // Store zip blob for GitHub push
  
  // Success popup state
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [successPopupText, setSuccessPopupText] = useState("");
  
  useEffect(() => {
    console.log("Location state:", location.state);
    console.log("Test case data from location:", location.state?.testCaseData);
    
    // Set projectType based on framework for Cypress JavaScript
    const framework = location.state?.framework;
    const projectTypeValue = framework === "cypress-javascript" ? "cypress-javascript" : (location.state?.projectType || "");
    setProjectType(projectTypeValue);
    setTestEnviorment(location.state?.testenvironmentdata || {});
    
    // Fix: Don't set testCase to empty object if testCaseData is undefined
    const testCaseData = location.state?.testCaseData;
    if (testCaseData) {
      setTestCase(testCaseData);
      console.log("Setting test case data:", testCaseData);
    } else {
      console.warn("No test case data found in location state");
    }
    
    setTestFiles(location.state?.testFilesData || "");
    setPageSource(location.state?.pageSource || "");
    setLocator(location.state?.generatedLocators || {});
    setPageClass(location.state?.generatedPageclassData || "");
    setProject(location.state?.generatedProjectData || "");
  }, [location.state]);

  // Auto-scroll to locators when they are generated
  // useEffect(() => {
  //   if (Object.entries(locator || {}).length > 0) {
  //     setTimeout(() => {
  //       const locatorsElement = document.querySelector('[data-locators-component]');
  //       if (locatorsElement) {
  //         locatorsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //       }
  //     }, 100);
  //   }
  // }, [locator]);

  const breadCrumbs = [
    {
      title: "Set Test Environment",
      status: Object.entries(testEnvironment).length !== 0 || confirmed ? "Completed" : "In Progress",
    },
    {
      title: "Test Case Details",
      status: Object.entries(testCase || {}).length > 0
        ? "Completed"
        : Object.entries(testEnvironment).length !== 0 && Object.entries(testCase || {}).length === 0
          ? "In Progress"
          : "Not Started",
    },
    {
      title: "Introduce SUT",
      status:
        !!pageSource && pageSource !== ""
          ? "Completed"
          : Object.entries(testCase).length > 0
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Generate Locators",
      status:
        Object.entries(locator).length > 0
          ? "Completed"
          : !!pageSource && pageSource !== ""
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Page Objects",
      status:
        !!pageClass && pageClass !== ""
          ? "Completed"
          : Object.entries(locator).length > 0
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Test Files",
      status:
        !!testFiles && testFiles !== ""
          ? "Completed"
          : !!pageClass && pageClass !== ""
            ? "In Progress"
            : "Not Started",
    },
    {
      title: "Cypress Project",
      status:
        !!project && project !== ""
          ? "Completed"
          : !!testFiles && testFiles !== ""
            ? "In Progress"
            : "Not Started",
    },
  ];

  const handleGenerateTestFiles = async () => {
    try {
      console.log("handleGenerateTestFiles called with testCase:", testCase);
      console.log("testCase type:", typeof testCase);
      console.log("testCase is array:", Array.isArray(testCase));
      console.log("testCase keys:", Object.keys(testCase));
      
      // Validate that we have test case data
      if (!testCase || Object.keys(testCase).length === 0) {
        console.error("No test case data available");
        setPopupMessage("No test case data available. Please upload test cases first.");
        setPopupOpen(true);
        return;
      }

      // Validate that we have page object data
      if (!pageClass || pageClass === "") {
        console.error("No page object data available");
        setPopupMessage("Page objects must be generated before creating test files. Please generate page objects first.");
        setPopupOpen(true);
        return;
      }

      const startProgress = (setProgress) => {
        setProgress(10);
        setTimeout(() => setProgress(30), 500);
        setTimeout(() => setProgress(50), 1000);
        setTimeout(() => setProgress(70), 1500);
        setTimeout(() => setProgress(90), 2000);
      };

      const finishTo100AndClose = async () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, progress: 100 }));
            setTimeout(() => {
              setLoadingState({ open: false, message: "", progress: null });
              resolve();
            }, 500);
          }, 500);
        });
      };

      setLoadingState({ open: true, message: "Generating Test Files...", progress: 0 });
      startProgress((progress) => setLoadingState((prev) => ({ ...prev, progress })));

      const requestData = { testCaseData: testCase, pageClassData: pageClass };
      console.log("Sending request data:", requestData);
      
      const testFilesGenerated = await cypressTestFilesWithToolCalling(requestData, location.state?.framework);
      const generatedTestFiles = testFilesGenerated.data.data; // Extract the data array from the response
      await finishTo100AndClose();
      navigate(location.pathname, { state: { ...location.state, testFilesData: generatedTestFiles }, replace: true });
    }
    catch (error) {
      console.error("Error in handleGenerateTestFiles:", error);
      const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      if (error.response?.data?.Error === "Your session has expired.") {
        // Handle session expired
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
    finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handlePageClass = async () => {
    try {
      const startProgress = (setProgress) => {
        setProgress(10);
        setTimeout(() => setProgress(30), 500);
        setTimeout(() => setProgress(50), 1000);
        setTimeout(() => setProgress(70), 1500);
        setTimeout(() => setProgress(90), 2000);
      };

      const finishTo100AndClose = async () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, progress: 100 }));
            setTimeout(() => {
              setLoadingState({ open: false, message: "", progress: null });
              resolve();
            }, 500);
          }, 500);
        });
      };

      setLoadingState({ open: true, message: "Generating Page Objects...", progress: 0 });
      startProgress((progress) => setLoadingState((prev) => ({ ...prev, progress })));

      const generatedPageClassGenerated = await cypressPageClassAPIWithToolCalling({ testFilesGenerated: testFiles, locators: locator }, location.state?.framework);
      const generatedPageClass = generatedPageClassGenerated.data.data; // Extract the data array from the response
      await finishTo100AndClose();
      navigate(location.pathname, { state: { ...location.state, generatedPageclassData: generatedPageClass }, replace: true });
    }
    catch (error) {
      const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      if (error.response?.data?.Error === "Your session has expired.") {
        // Handle session expired
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
    finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleProjectGeneration = async () => {
    try {
      setLoadingState({ open: true, message: "Generating Cypress Project...", progress: 0 });
      const startProgress = (setProgress) => {
        setProgress(10);
        setTimeout(() => setProgress(30), 500);
        setTimeout(() => setProgress(50), 1000);
        setTimeout(() => setProgress(70), 1500);
        setTimeout(() => setProgress(90), 2000);
      };

      const finishTo100AndClose = async () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            setLoadingState((prev) => ({ ...prev, progress: 100 }));
            setTimeout(() => {
              setLoadingState({ open: false, message: "", progress: null });
              resolve();
            }, 500);
          }, 500);
        });
      };

      startProgress((progress) => setLoadingState((prev) => ({ ...prev, progress })));

      console.log("Calling cypressProjectAPIWithToolCalling with:", { 
        testFilesGenerated: testFiles, 
        pageClassData: { "pageClassGenerated": pageClass },
        testCaseData: testCase,
        testEnvironmentData: testEnvironment
      });
      const generatedProjectGenerated = await cypressProjectAPIWithToolCalling({ 
        testFilesGenerated: testFiles, 
        pageClassData: { "pageClassGenerated": pageClass },
        testCaseData: testCase,
        testEnvironmentData: testEnvironment
      }, location.state?.framework);
      const generatedProject = generatedProjectGenerated.data;
      console.log("Received project data from backend:", generatedProject);
      console.log("Project data type:", typeof generatedProject);
      console.log("Project data is array:", Array.isArray(generatedProject));
      
      await finishTo100AndClose();
      
      // Automatically download the project after generation
      if (generatedProject && generatedProject !== "") {
        console.log("Auto-downloading Cypress project with:", generatedProject);
        const success = downloadCypressProject(generatedProject, setZipBlob);
        if (success) {
          setSuccessPopupText("Cypress");
          setSuccessPopupOpen(true);
        } else {
          setPopupMessage("Project generated but download failed. Please try again.");
          setPopupOpen(true);
        }
      }
      
      navigate(location.pathname, { state: { ...location.state, generatedProjectData: generatedProject }, replace: true });
    }
    catch (error) {
      console.error("Error in handleProjectGeneration:", error);
      const errorMsg = error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      if (error.response?.data?.Error === "Your session has expired.") {
        // Handle session expired
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
    finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleDownloadClick = () => {
    console.log("handleDownloadClick called");
    console.log("project state:", project);
    console.log("project type:", typeof project);
    console.log("project is array:", Array.isArray(project));
    
    if (project && project !== "") {
      // Download the Cypress project
      console.log("Calling downloadCypressProject with:", project);
      const success = downloadCypressProject(project, setZipBlob);
      if (success) {
        setPopupMessage("Cypress project downloaded successfully!");
        setPopupOpen(true);
      } else {
        setPopupMessage("Error downloading Cypress project. Please try again.");
        setPopupOpen(true);
      }
    } else {
      console.warn("Project data is empty or undefined");
      setPopupMessage("Please generate the Cypress project first.");
      setPopupOpen(true);
    }
  };

  const handleGitHubProject = async () => {
    if (!zipBlob) {
      console.error("ZIP file not generated yet.");
      return;
    }
    navigate(`/dashboard/test-automation/newproject-gitcredentials`, {
      state: {
        framework: location.state?.framework,
        projectType: location.state?.projectType,
        zipFile: zipBlob,
      },
    });
  };

  const testcaseNextButton = {
    label: "Test Case Imported",
    onClick: null,
    startIcon: null,
    variant: "text",
    sx: { float: "right" },
    disabled: true,
  };

  const testFilesButton = {
    label: "Generate Test Files",
    onClick: handleGenerateTestFiles,
    startIcon: <GenerateButton />,
    variant: "contained",
    sx: { float: "right" },
    disabled: !(!!pageClass && pageClass !== ""),
  };

  const cypressDownloadButton = {
    label: "Download",
    onClick: handleDownloadClick,
    variant: "text",
    startIcon: <SaveAltRoundedIcon />,
    sx: { textTransform: "none" },
  };

  const locatorsNextButton = {
    label: "Generate Page Objects",
    variant: "contained",
    onClick: handlePageClass,
    startIcon: <GenerateButton />,
    sx: { float: "right" },
  };

  const projectNextButton = {
    label: "Generate Project",
    variant: "contained",
    onClick: handleProjectGeneration,
    startIcon: <GenerateButton />,
    sx: { marginLeft: "auto", marginRight: 1 },
  };

  const githubButton = {
    label: "Push to GitHub",
    variant: "contained",
    onClick: handleGitHubProject,
    startIcon: <GenerateButton />,
    sx: { marginRight: 1, whiteSpace: "nowrap" },
    disabled: !zipBlob, // Disable if zipBlob is not available
  };



  const formatTestPlan = (testPlanData) => {
    const formattedTestPlan = {};
    Object.entries(testPlanData).map(([key, value]) => {
      if (Array.isArray(value)) {
        return (formattedTestPlan[key] = flattenArray(value));
      }
      if (!!value && typeof value === "object") {
        return (formattedTestPlan[key] = flattenObject(value));
      }
      return (formattedTestPlan[key] = value);
    });
    return formattedTestPlan;
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexGrow: 1,
      }}
    >
      <Box sx={{ maxHeight: "100%", width: "100%" }}>
        {/* Fixed breadcrumb where you want it */}
        <Box
          sx={{
            position: "sticky",
            top: "0",
            width: "100%",
            zIndex: 1000,
            bgcolor: "white",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <QMentisBreadCrumb breadCrumbs={breadCrumbs} sx={{ height: "56px" }} />
        </Box>
        <Box
          sx={{
            display: "flex",
            bgcolor: "#f6f3f2",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            overflowY: "auto",
            width: "100%",
            height: "80vh",
            minHeight: "80vh",
            position: "relative",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "4px",
              minHeight: "20px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "600px",
              margin: "10px auto",
              "& > *:not(:last-child)": {
                marginBottom: "16px",
              },
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={0.1}
            >
              <Box display="flex" alignItems="center" mb={0.1}>
                <AutomateIcon style={{ marginRight: 4 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ mt: 0.5 }}
                >
                  Generate automated test scripts
                </Typography>
              </Box>
              <FullscreenToggle />
            </Box>

            {/* CYPRESS JAVASCRIPT COMPONENTS */}
            <TestEnvironmentForm
              testEnvironmentData={testEnvironment}
              setTestEnviorment={setTestEnviorment}
              framework={location.state?.framework}
              projectType={location.state?.projectType}
              confirmed={confirmed}
              setConfirmed={setConfirmed}
            />

            {confirmed && (
              <UploadBox
                testCaseData={testCase}
                setTestCase={setTestCase}
                testcaseNextButton={testcaseNextButton}
                javaSeliniumDownloadButton={cypressDownloadButton}
              />
            )}
            {Object.entries(testCase || {}).length > 0 && <TestCaseUI testCaseData={testCase} setTestCase={setTestCase} testcaseNextButton={testcaseNextButton} javaSeliniumDownloadButton={cypressDownloadButton} projectType={projectType} />}
            {Object.entries(testCase || {}).length > 0 && <InputForm setPageSource={setPageSource} framework={location.state?.framework} />}
            {Object.entries(locator || {}).length > 0 && (
              <div data-locators-component>
                <Locators locator={locator} setLocator={setLocator} locatorsNextButton={locatorsNextButton} projectType={projectType}/>
              </div>
            )}
            {!!pageClass && pageClass !== "" && <PageClass pageClass={pageClass} setPageClass={setPageClass} featureFile={testFiles}
              stepDefinitionNextButton={testFilesButton}
              projectType={projectType}
            />}
            {!!testFiles && testFiles !== "" && <Feature featureFile={testFiles} setFeatureFile={setTestFiles} projectType={projectType} onGenerateProject={handleProjectGeneration} onPushToGitHub={handleGitHubProject} zipBlob={zipBlob}/>}

          </Box>
        </Box>
        <Container
          sx={{
            width: "100%",
          }}
        >
        </Container>
      </Box>
      <Box
        component="footer"
        sx={{
          width: "100%",
          maxWidth: "601px",
          backgroundColor: "#f6f3f2",
          textAlign: "center",
          paddingX: "5px",
          position: "fixed",
          bottom: "0",
          zIndex: "99",
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Use QMentisAI™ with human supervision, as with all generative AI tools.
        </Typography>
      </Box>
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />

      <SuccessPopup
        open={successPopupOpen}
        handleClose={() => setSuccessPopupOpen(false)}
        text={successPopupText}
      />
    </Box>
  );
};

export default CypressJavaScriptNewProjectOutputPage; 